/**
 * DeSight: Varianten-Bestätigungs-Modal
 * Zeigt eine Bestätigungsabfrage bevor ein Produkt mit Varianten in den Warenkorb gelegt wird
 */

class VariantConfirmationModal extends HTMLElement {
  constructor() {
    super();
    this.formId = this.getAttribute('data-form-id');
    this.form = null;
    this.confirmed = false;
    this.boundHandler = this.handleButtonClick.bind(this);
  }

  connectedCallback() {
    this.form = document.getElementById(this.formId);

    if (!this.form) {
      console.warn('VariantConfirmationModal: Form not found:', this.formId);
      return;
    }

    this.bindEvents();
    this.observeNewButtons();
  }

  bindEvents() {
    // Initiale Buttons binden
    this.bindSubmitButtons();

    // Modal Buttons
    this.querySelector('[data-action="close"]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-action="cancel"]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-action="confirm"]')?.addEventListener('click', () => this.confirm());

    // Overlay Click schließt Modal
    this.querySelector('.variant-confirmation-modal__overlay')?.addEventListener('click', () => this.close());

    // ESC Taste
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) {
        this.close();
      }
    });
  }

  bindSubmitButtons() {
    // Alle Submit-Buttons für dieses Formular finden (auch Sticky-Bar)
    const buttons = document.querySelectorAll(
      `[form="${this.formId}"][type="submit"], #${this.formId} [type="submit"]`
    );

    buttons.forEach(btn => {
      // Verhindere doppeltes Binding
      if (!btn.hasAttribute('data-confirmation-bound')) {
        btn.setAttribute('data-confirmation-bound', 'true');
        btn.addEventListener('click', this.boundHandler, true); // capture: true
      }
    });
  }

  observeNewButtons() {
    // MutationObserver für dynamisch hinzugefügte Buttons (z.B. Sticky-Bar)
    const observer = new MutationObserver(() => {
      this.bindSubmitButtons();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  handleButtonClick(e) {
    // Wenn bereits bestätigt, normal durchlassen
    if (this.confirmed) {
      this.confirmed = false;
      return;
    }

    // STOPPE alles - verhindere dass das Theme's AJAX Cart fired
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Speichere den geklickten Button für später
    this.clickedButton = e.currentTarget;

    this.open();

    return false;
  }

  open() {
    this.updateVariantInfo();
    this.hidden = false;
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
      this.querySelector('[data-action="cancel"]')?.focus();
    }, 100);
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
  }

  confirm() {
    this.confirmed = true;
    this.close();

    // Klicke den originalen Button erneut
    if (this.clickedButton) {
      this.clickedButton.click();
    }
  }

  updateVariantInfo() {
    const variantInput = this.form.querySelector('input[name="id"]');
    if (!variantInput) return;

    const variantId = variantInput.value;
    const productData = this.getProductData();
    if (!productData) return;

    const variant = productData.variants.find(v => v.id == variantId);
    if (!variant) return;

    const titleEl = this.querySelector('[data-variant-title]');
    const priceEl = this.querySelector('[data-variant-price]');

    if (titleEl) titleEl.textContent = variant.title;
    if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
  }

  getProductData() {
    const productJson = this.form.closest('[data-product-json]')?.querySelector('script[type="application/json"]');
    if (productJson) {
      try {
        return JSON.parse(productJson.textContent);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(amount);
  }
}

if (!customElements.get('variant-confirmation-modal')) {
  customElements.define('variant-confirmation-modal', VariantConfirmationModal);
}

